import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { CreateEventDto, EventLifecycleStatus } from '../../types/events.types';
import { useIdempotentAppMutation } from '../use-idempotent-app-mutation.hook';
import { pendingEventsStore, PendingMutationSettleOutcome } from './pending-events.store';
import { useOptimisticEvents } from './use-optimistic-events';

type UpdateEventVars = {
  companyId: string;
  data: CreateEventDto;
  eventId: string;
  // Fires once when the queued EVENT_MUTATION apply settles (list poll observes it), not when
  // the PATCH resolves — success/failure toasts belong here so they match the row state.
  onApplySettled?: (outcome: PendingMutationSettleOutcome) => void;
};
type OptimisticRollback = { rollback: () => void };

export const useUpdate = () => {
  const optimistic = useOptimisticEvents();

  const { mutate: update, isPending } = useIdempotentAppMutation<
    unknown,
    HttpException,
    UpdateEventVars,
    OptimisticRollback
  >([QueryKey.PATCH_EVENT], {
    mutationFn: ({ companyId, data, eventId }, { headers }) =>
      ApiService.patch(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'events', eventId] },
        {
          body: data,
          headers,
        },
      ),
    // Optimistically mark the row pending-edit (renders the create-style skeleton in its place);
    // polling settles it to active/failed.
    // Also register the edit in the pending store so useEvents keeps polling and re-invalidates
    // the derived queries (snapshots/graph) after the async EVENT_MUTATION apply — the server
    // may settle before any fetch observes a pending lifecycle status.
    onMutate: async ({ companyId, eventId, onApplySettled }) => {
      await optimistic.cancel();
      const previous = optimistic.snapshot();
      optimistic.setStatus(eventId, EventLifecycleStatus.PENDING_EDIT);
      pendingEventsStore
        .getState()
        .addPendingEdit({ companyId, eventId, onApplySettled, requestedAtMs: Date.now() });
      return { rollback: () => optimistic.restore(previous) };
    },
    // Settle detection only trusts fetches newer than this acknowledgement (see useEvents).
    onSuccess: (_data, { eventId }) => {
      pendingEventsStore.getState().markPendingEditEnqueued(eventId);
    },
    onError: (_error, variables, context) => {
      pendingEventsStore.getState().removePendingEdit(variables.eventId);
      context?.rollback();
    },
  });

  return { update, isPending };
};
